import { getLoyalties } from '@/services/loyalty.service';

const KNOWN_LOYALTY_TYPES = ['discount', 'bonus'];

function normalizeLoyaltyType(value) {
  const type = String(value ?? '').trim();
  return KNOWN_LOYALTY_TYPES.includes(type) ? type : null;
}

function cloneLoyaltyData(value) {
  if (!value || typeof value !== 'object') {
    return value;
  }

  return JSON.parse(JSON.stringify(value));
}

export function normalizeLoyaltyStatus(status) {
  if (Array.isArray(status)) {
    return status.map(normalizeLoyaltyType).filter(Boolean);
  }

  const directType = normalizeLoyaltyType(status);
  if (directType) {
    return [directType];
  }

  if (!status || typeof status !== 'object') {
    return [];
  }

  const candidates = [
    status.active,
    status.type,
    status.loyaltyType,
    status.loyalty,
    status.loyaltyProgram,
    status.program,
    status.status,
    status.data,
  ];

  for (const candidate of candidates) {
    if (candidate === status) {
      continue;
    }

    const normalized = normalizeLoyaltyStatus(candidate);
    if (normalized.length) {
      return normalized;
    }
  }

  return [];
}

export default {
  namespaced: true,

  state: () => ({
    current: null,
    status: [],
    isCurrentLoaded: false,
    isLoaded: false,
    isLoading: false,
    request: null,
    currentRequest: null,
  }),

  getters: {
    current(state) {
      return state.current;
    },
    status(state) {
      return state.status;
    },
    isCurrentLoaded(state) {
      return state.isCurrentLoaded;
    },
    isLoaded(state) {
      return state.isLoaded;
    },
  },

  mutations: {
    SET_CURRENT(state, current) {
      state.current = current && typeof current === 'object' ? current : null;
      state.isCurrentLoaded = Boolean(state.current);
      state.status = normalizeLoyaltyStatus(state.current);
      state.isLoaded = state.isCurrentLoaded;
    },
    SET_STATUS(state, status) {
      state.status = normalizeLoyaltyStatus(status);
      state.isLoaded = true;
    },
    SET_LOADING(state, value) {
      state.isLoading = value;
    },
    SET_REQUEST(state, request) {
      state.request = request;
    },
    SET_CURRENT_REQUEST(state, request) {
      state.currentRequest = request;
    },
    PATCH_PROGRAM(state, { type, data }) {
      if (!state.current || !type || !data) return;
      state.current = {
        ...state.current,
        [type]: { ...state.current[type], ...data },
      };
    },
    CLEAR_STATUS(state) {
      state.current = null;
      state.status = [];
      state.isCurrentLoaded = false;
      state.isLoaded = false;
      state.isLoading = false;
      state.request = null;
      state.currentRequest = null;
    },
  },

  actions: {
    async fetchCurrent({ state, commit }, payload = {}) {
      const { force = false } = payload || {};

      if (!force && state.isCurrentLoaded) {
        return cloneLoyaltyData(state.current);
      }

      if (!force && state.currentRequest) {
        return state.currentRequest;
      }

      const request = getLoyalties()
        .then((current) => {
          if (!current) {
            commit('CLEAR_STATUS');
            return null;
          }

          commit('SET_CURRENT', current);
          return cloneLoyaltyData(current);
        })
        .finally(() => {
          commit('SET_LOADING', false);
          commit('SET_CURRENT_REQUEST', null);
        });

      commit('SET_LOADING', true);
      commit('SET_CURRENT_REQUEST', request);

      return request;
    },

    async fetchProgram({ dispatch }, payload = {}) {
      const options = typeof payload === 'string' ? { type: payload } : payload || {};
      const current = await dispatch('fetchCurrent', options);
      const type = options.type || current?.active?.[0];

      return cloneLoyaltyData(type ? current?.[type] || {} : {});
    },

    async fetchLevels({ dispatch }, payload = {}) {
      const program = await dispatch('fetchProgram', payload);
      return cloneLoyaltyData(Array.isArray(program?.levels) ? program.levels : []);
    },

    async fetchStatus({ state, commit, dispatch }, payload = {}) {
      const { force = false } = payload || {};

      if (!force && state.isLoaded) {
        return state.status;
      }

      if (!force && state.request) {
        return state.request;
      }

      const request = dispatch('fetchCurrent', { force })
        .then((current) => {
          if (!current) {
            commit('CLEAR_STATUS');
            return [];
          }

          const status = normalizeLoyaltyStatus(current);
          commit('SET_STATUS', status);
          return status;
        })
        .finally(() => {
          commit('SET_REQUEST', null);
        });

      commit('SET_REQUEST', request);

      return request;
    },

    patchProgram({ commit }, { type, data }) {
      commit('PATCH_PROGRAM', { type, data });
    },

    setCurrent({ commit }, current) {
      commit('SET_CURRENT', current);
    },

    setStatus({ commit }, status) {
      commit('SET_STATUS', status);
    },

    clearStatus({ commit }) {
      commit('CLEAR_STATUS');
    },
  },
};
